<template>
  <el-dialog :visible="true" :title="baseline ? 'Manage placement · ' + displayName : 'New placement period'"
             width="760px" custom-class="top-placement-dialog" append-to-body
             :close-on-click-modal="false" :close-on-press-escape="!busy" :show-close="!busy" :before-close="close">
    <p v-if="loading" role="status">Loading the current period…</p>
    <p v-if="error" class="editor-error" role="alert">{{ error }}</p>
    <p v-if="notice" class="editor-success" role="status">{{ notice }}</p>
    <template v-if="!loading && (baseline || !row)">
      <div v-if="baseline" class="editor-summary">
        <strong>{{ displayName }} · #{{ baseline.clinic_id }}</strong>
        <span class="editor-status">{{ baseline.status }}</span>
        <small v-if="baseline.confirmed_by">Confirmed by {{ baseline.confirmed_by }}</small>
      </div>
      <form @submit.prevent="submit('save')">
        <fieldset :disabled="busy || needsReload || cancelled">
          <div v-if="!baseline" class="editor-field">
            <span>Clinic</span>
            <el-select v-model="form.clinic_id" filterable remote :remote-method="searchClinics" :loading="clinicSearch.loading"
                       placeholder="Name or clinic ID" aria-label="Placement clinic" :disabled="busy || needsReload">
              <el-option v-for="clinic in clinicSearch.view || []" :key="clinic.id" :value="clinic.id"
                         :label="clinicLabel(clinic) + ' (#' + clinic.id + ')'"/>
            </el-select>
            <small v-if="clinicSearch.error" role="alert">Clinic search failed. Please try again.</small>
          </div>
          <div class="editor-grid">
            <div class="editor-field"><label for="placement-first-day">First day</label>
              <el-date-picker id="placement-first-day" v-model="form.starts_on" type="date" format="dd.MM.yyyy" value-format="yyyy-MM-dd"
                              :editable="false" :disabled="busy || needsReload || cancelled" placeholder="Choose first day"
                              aria-label="First day"/>
            </div>
            <div class="editor-field"><label for="placement-last-day">Last day</label>
              <el-date-picker id="placement-last-day" v-model="form.ends_on" type="date" format="dd.MM.yyyy" value-format="yyyy-MM-dd"
                              :editable="false" :disabled="busy || needsReload || cancelled" placeholder="Choose last day"
                              aria-label="Last day"/>
            </div>
            <label>Weight (0–100) <input v-model.number="form.weight" type="number" min="0" max="100" step="1" required aria-label="Weight"></label>
            <label>Payment reference <input v-model="form.payment_reference" type="text" :readonly="!!baseline" :required="!baseline" aria-label="Payment reference"></label>
          </div>
          <p class="editor-hint">Dates include both days and use Kyiv time. Higher weight appears first.</p>
          <label class="editor-check"><input v-model="form.all_directions" type="checkbox">All directions</label>
          <div v-if="!form.all_directions" class="editor-field">
            <span>Directions</span>
            <el-select v-model="form.illness_kind_ids" multiple filterable placeholder="Choose directions" aria-label="Placement directions"
                       :disabled="busy || needsReload || cancelled">
              <el-option v-for="direction in directionOptions" :key="direction.id" :value="direction.id" :label="direction.name"/>
            </el-select>
          </div>
          <p class="editor-hint">All directions also includes directions linked to the clinic later. A selected list limits this period to those directions; the site still checks the clinic’s links.</p>
          <label class="editor-check"><input v-model="form.show_on_homepage" type="checkbox">Show on homepage</label>
          <p v-if="validationMessage" class="editor-error" role="alert">{{ validationMessage }}</p>
          <button v-if="!cancelled" type="submit" class="editor-button primary" :disabled="!canSave">
            {{ busy ? 'Please wait…' : baseline ? 'Save changes' : 'Create draft' }}
          </button>
        </fieldset>
      </form>
      <p v-if="!baseline" class="editor-hint">A new period is a draft. Confirm it separately after verifying payment.</p>
      <div v-if="baseline && !cancelled" class="editor-lifecycle">
        <p>Change status</p>
        <div class="editor-actions">
          <button v-for="action in actions" :key="action" type="button" class="editor-button"
                  :class="{ danger: action === 'cancel' }" :disabled="busy || needsReload || dirty"
                  @click="reviewAction(action)">{{ labels[action] }}</button>
        </div>
        <small v-if="dirty">Save or reload your edits before changing the status.</small>
        <div v-if="pendingAction" class="editor-confirm" role="group" aria-label="Review status change">
          <p>{{ confirmation }}</p>
          <button type="button" class="editor-button" :class="pendingAction === 'cancel' ? 'danger' : 'primary'"
                  :disabled="busy || needsReload" @click="submit(pendingAction)">Yes, {{ labels[pendingAction].toLowerCase() }}</button>
          <button type="button" class="editor-button" :disabled="busy" @click="pendingAction = ''">Keep current status</button>
        </div>
      </div>
    </template>
    <div slot="footer" class="editor-footer">
      <button v-if="row || baseline" type="button" class="editor-button" :disabled="busy" @click="reload">Reload period</button>
      <button type="button" class="editor-button" :disabled="busy" @click="close">Close</button>
    </div>
  </el-dialog>
</template>

<script>
import api from '@/api/modules/clinics';
import { requestState, loadLatest, invalidate, translatedName } from './topPlacementOverview';
import { blankPeriod, periodForm, formError, editPayload, availableActions, readPeriod, changePeriod, createPeriod, mutationError } from './topPlacementManagement';

export default {
  name: 'TopPlacementEditor',
  props: { row: { type: Object, default: null }, clinicName: { type: String, default: '' }, catalog: { type: Array, default: () => [] } },
  data: () => ({ baseline: null, form: blankPeriod(), loading: false, submitting: false, needsReload: false,
    error: '', notice: '', pendingAction: '', generation: 0, disposed: false, clinicSearch: requestState(),
    labels: { confirm: 'Confirm payment', pause: 'Pause', resume: 'Resume', cancel: 'Cancel period' } }),
  computed: {
    displayName() {
      const clinic = (this.clinicSearch.view || []).find(item => item.id === this.form.clinic_id);
      return this.clinicName || (clinic ? this.clinicLabel(clinic) : 'Clinic #' + this.form.clinic_id);
    },
    busy() { return this.loading || this.submitting; },
    cancelled() { return this.baseline?.status === 'cancelled'; },
    actions() { return availableActions(this.baseline); },
    dirty() {
      if (!this.baseline) return true;
      try { return editPayload(this.baseline, this.form) !== null; } catch (_) { return true; }
    },
    validationMessage() {
      const editing = this.baseline || this.form.clinic_id || this.form.starts_on || this.form.ends_on || this.form.payment_reference;
      return editing && !this.cancelled ? formError(this.form, !this.baseline) : '';
    },
    canSave() { return !this.busy && !this.needsReload && !this.cancelled && !formError(this.form, !this.baseline) && this.dirty; },
    directionOptions() {
      const options = this.catalog.slice();
      this.form.illness_kind_ids.forEach(id => { if (!options.some(item => item.id === id)) options.push({ id, name: 'Direction #' + id }); });
      return options;
    },
    confirmation() {
      return {
        confirm: 'Confirm that payment has been verified? This reserves slots and records your name.',
        pause: 'Pause this period? Its slots will be released until it is resumed.',
        resume: 'Resume this period? Available slots will be checked again.',
        cancel: 'Cancel this period? It will stop reserving slots and remain in history. It cannot be resumed.',
      }[this.pendingAction] || '';
    },
  },
  mounted() { if (this.row) this.reload(); },
  beforeDestroy() { this.disposed = true; this.generation++; invalidate(this.clinicSearch); },
  methods: {
    clinicLabel(clinic) { return translatedName(clinic.name) || 'Clinic #' + clinic.id; },
    searchClinics(query) {
      if (!query.trim()) { invalidate(this.clinicSearch); return; }
      return loadLatest(this.clinicSearch, () => api.getClinics(/^\d+$/.test(query) ?
        { clinic_ids: [Number(query)], limit: 20 } : { clinic_name: query, limit: 20 }), data => {
        if (!data || data.error || !Array.isArray(data.clinics)) throw new Error('Clinic search failed');
        return data.clinics;
      });
    },
    async reload() {
      if (this.busy) return;
      const target = this.baseline || this.row;
      if (!target) return;
      const generation = ++this.generation;
      this.loading = true; this.error = ''; this.notice = ''; this.pendingAction = ''; this.needsReload = true;
      try {
        const row = await readPeriod(api, target);
        if (this.disposed || generation !== this.generation) return;
        this.baseline = row; this.form = periodForm(row); this.needsReload = false;
      } catch (error) { if (!this.disposed && generation === this.generation) this.error = mutationError(error); }
      finally { if (!this.disposed && generation === this.generation) this.loading = false; }
    },
    reviewAction(action) { if (!this.busy && !this.needsReload && !this.dirty && this.actions.includes(action)) this.pendingAction = action; },
    async submit(action) {
      if (this.busy || this.needsReload || this.cancelled) return;
      if (action === 'save' ? !this.canSave : action !== this.pendingAction || this.dirty || !this.actions.includes(action)) return;
      this.submitting = true; this.error = ''; this.notice = '';
      let wrote = false;
      const onWrite = () => {
        if (this.disposed) throw new Error('Editor closed.');
        wrote = true; this.$emit('mutating');
      };
      try {
        const row = this.baseline ? await changePeriod(api, this.baseline, this.form, action, onWrite) : await createPeriod(api, this.form, onWrite);
        if (this.disposed) return;
        this.baseline = row; this.form = periodForm(row); this.pendingAction = '';
        this.notice = action === 'save' ? 'Saved and verified.' : 'Status updated and verified.';
      } catch (error) {
        if (!this.disposed) {
          this.error = mutationError(error);
          this.needsReload = !!this.baseline || wrote;
          if (wrote && !this.baseline) this.error += ' Close and refresh the registry before trying again.';
        }
      } finally {
        if (!this.disposed) { this.submitting = false; if (wrote) this.$emit('settled'); }
      }
    },
    close(done) {
      if (this.busy) return;
      this.$emit('close');
      if (typeof done === 'function') done();
    },
  },
};
</script>

<style>
.top-placement-dialog { max-width: calc(100vw - 32px); }
</style>
<style scoped>
fieldset { border: 0; padding: 0; min-width: 0; }
.editor-summary { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; margin-bottom: 22px; }
.editor-summary small { width: 100%; }
.editor-status { background: #edf2f7; border-radius: 4px; padding: 4px 8px; }
.editor-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.editor-grid label, .editor-field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; margin-bottom: 12px; }
.editor-grid input { min-width: 0; width: 100%; padding: 9px 10px; height: 40px; border: 1px solid #bcc8d4; border-radius: 5px; background: white; color: #34465a; }
.editor-grid input[readonly] { background: #f5f7fa; }
.editor-grid .el-date-editor { width: 100%; }
.editor-check { display: flex; align-items: center; gap: 8px; margin: 16px 0; }
.editor-check input { width: 16px; height: 16px; }
.editor-hint { font-size: 12px; line-height: 1.6; color: #657389; margin: 10px 0; }
.editor-button { border: 1px solid #b4c7d7; border-radius: 5px; padding: 9px 14px; background: white; color: #266b9e; cursor: pointer; }
.editor-button.primary { background: #287aba; color: white; border-color: #287aba; }
.editor-button.danger { color: #b23737; border-color: #e6b8b8; }
.editor-button:disabled { opacity: .5; cursor: default; }
.editor-lifecycle { margin-top: 22px; border-top: 1px solid #e1e7ee; padding-top: 16px; }
.editor-actions, .editor-footer { display: flex; flex-wrap: wrap; gap: 8px; }
.editor-footer { justify-content: flex-end; }
.editor-confirm { background: #fff8eb; border-radius: 6px; padding: 14px; margin-top: 14px; }
.editor-confirm .editor-button { margin-right: 8px; }
.editor-error { color: #b23737; background: #fff5f4; padding: 12px; border-radius: 5px; }
.editor-success { color: #277047; background: #e8f5ec; padding: 12px; border-radius: 5px; }
@media (max-width: 600px) { .editor-grid { grid-template-columns: 1fr; } }
</style>
